import { useCallback, useMemo } from 'react'
import { CornerDownRight } from 'lucide-react'
import { cn } from '@global/utils/tailwind'
import { CandidateInputsTooltipTitle, LinearProgress } from '@taxonomy-translation/components'
import { Tooltip } from '@components/tooltip/tooltip'
import {
  Field,
  FieldTranslation,
  TaxonomyTranslationInput,
  TranslationStatus,
} from '@taxonomy-translation/types'
import { formatInputNames, isListField, isListTranslation } from '@taxonomy-translation/utils'
import TwoStarsSVG from 'images/two-stars.svg'
import { ReviewItemSelect } from './review-item-select'
import { t } from '@lingui/core/macro'

interface ReviewStepProps {
  step?: number
  total?: number
  listValueIndex?: number
  translation: FieldTranslation
  referenceFields?: Field[]
  inputs: TaxonomyTranslationInput[]
  showExcludeWarning?: boolean
  selectedTranslated: { value: string; label: string } | null
  selectedListValue: { value: string; label: string } | null
  onTranslatedChange: (value: { value: string; label: string } | null) => void
  onListValueChange: (value: { value: string; label: string } | null) => void
}

export const ReviewStep = ({
  step = 0,
  total = 0,
  listValueIndex,
  translation,
  referenceFields = [],
  inputs,
  showExcludeWarning = false,
  selectedTranslated,
  selectedListValue,
  onTranslatedChange,
  onListValueChange,
}: ReviewStepProps) => {
  const isList = isListTranslation(translation)
  const isListValueEditing = isList && typeof listValueIndex === 'number'
  const listValueTranslation = useMemo(
    () => (isListValueEditing ? translation.listValueTranslations[listValueIndex] : null),
    [translation, listValueIndex, isListValueEditing],
  )

  const progress = ((step / total) * 100).toFixed(2)

  // TODO: Refactor to use the useFilteredReferenceFields hook for cleaner and reusable filtering logic
  const channelOptions = useMemo(
    () => referenceFields?.filter(field => field.channel === translation.channel) || [],
    [referenceFields, translation.channel],
  )

  const noChannelOptions = useMemo(
    () => referenceFields?.filter(field => field.channel === null) || [],
    [referenceFields],
  )

  const filteredOptions = useMemo(
    () => [...channelOptions, ...(translation.channel !== null ? noChannelOptions : [])],
    [channelOptions, noChannelOptions, translation.channel],
  )

  const fieldTranslationOptions = useMemo(() => {
    const values = filteredOptions.map(field => field.name)

    if (translation.candidate && !values.includes(translation.candidate)) {
      values.push(translation.candidate)
    }

    return values.map(value => ({
      value,
      label: value,
    }))
  }, [translation, filteredOptions])

  const listValueTranslationOptions = useMemo(() => {
    const selectedField = filteredOptions.find(({ name }) => name === selectedTranslated?.value)
    const values =
      selectedField && isListField(selectedField)
        ? selectedField.listValues.map(listValue => listValue[0] ?? '')
        : []

    if (listValueTranslation?.candidate && !values.includes(listValueTranslation.candidate)) {
      values.push(listValueTranslation.candidate)
    }

    return values.map(value => ({
      value,
      label: value,
    }))
  }, [selectedTranslated, filteredOptions, listValueTranslation])

  const description = isListValueEditing
    ? translation.listValueTranslations?.[listValueIndex]?.matchDescription
    : translation.matchDescription

  const renderStatus = useCallback(
    (status: TranslationStatus) => (
      <div
        className={cn('h-[15px] rounded-[7.5px] flex items-center px-2 ml-4', {
          'bg-red-100': status === TranslationStatus.NoMatch,
          'bg-green-100': status === TranslationStatus.Match,
          'bg-blue-100': status === TranslationStatus.Reviewed,
          'bg-slate-200': status === TranslationStatus.Excluded,
        })}
      >
        <p className="text-xs">{TRANSLATION_STATUS_LABELS[status]}</p>
      </div>
    ),
    [],
  )

  const candidateInputsNames = useMemo(
    () =>
      formatInputNames(
        inputs.filter(
          input => input.type === 'candidate_trafficking' || input.type === 'candidate_list_values',
        ),
      ),
    [inputs],
  )

  return (
    <div className="px-14">
      <div className="-mx-14">
        <LinearProgress progress={progress} />
      </div>

      <div className="flex mt-12 mb-7 items-center">
        <h3 className="font-semibold text-[22px]">{`${step} of ${total}`}</h3>
        {isListValueEditing ? null : renderStatus(translation.status)}
      </div>

      <div className={cn('grid grid-cols-6 gap-0', { 'opacity-50': isListValueEditing })}>
        <p className={cn(columnHeaderClassName, 'pr-2')}>{t`Stream`}</p>
        <div className={cn(columnHeaderClassName, 'px-2')}>
          <Tooltip
            title={<CandidateInputsTooltipTitle names={candidateInputsNames} />}
            disabled={isListValueEditing}
          >
            {t`Data to be translated field name`}
          </Tooltip>
        </div>
        <p className={cn(columnHeaderClassName, 'px-2')}>{t`Baseline field name match`}</p>
        <p className={cn(columnHeaderClassName, 'px-2')}>{t`Translated field name`}</p>
        <p className={cn(columnHeaderClassName, 'px-2')}>{t`List values`}</p>
        <p className={cn(columnHeaderClassName, 'pl-2')}>{t`Match confidence`}</p>
        <p className={cn(rowClassName, 'pr-2')}>{translation.channel}</p>
        <p className={cn(rowClassName, 'px-2')}>{translation.candidate}</p>
        <p className={cn(rowClassName, 'px-2')}>{translation.reference}</p>
        {isListValueEditing ? (
          <p className={cn(rowClassName, 'px-2')}>{selectedTranslated?.value || ''}</p>
        ) : (
          <div className={cn(rowClassName, 'px-2')}>
            <ReviewItemSelect
              value={selectedTranslated}
              options={fieldTranslationOptions}
              onChange={onTranslatedChange}
            />
          </div>
        )}
        <p className={cn(rowClassName, 'px-2')}>
          {isList ? translation.listValueTranslations?.length : ''}
        </p>
        <p className={cn(rowClassName, 'pl-2')}>
          {isListValueEditing || !translation.confidence
            ? null
            : `${translation.confidence * 100}%`}
        </p>
      </div>

      {isListValueEditing && listValueTranslation ? (
        <>
          <div className="flex items-center mt-8 mb-4 ml-20">
            <CornerDownRight className="h-[1rem]" />
            <p className="text-base">
              {t`List value`}
              <span className="font-bold">{listValueIndex + 1}</span> {t`of`}{' '}
              {translation.listValueTranslations.length}
            </p>
            {isListValueEditing && translation.listValueTranslations[listValueIndex]
              ? renderStatus(translation.listValueTranslations[listValueIndex].status)
              : null}
          </div>

          <div className="grid grid-cols-4 gap-0 ml-20">
            <div className={cn(columnHeaderClassName, 'px-2')}>
              <Tooltip title={<CandidateInputsTooltipTitle names={candidateInputsNames} />}>
                {t`Data to be translated list value`}
              </Tooltip>
            </div>
            <p className={cn(columnHeaderClassName, 'px-2')}>{t`Baseline list value match`}</p>
            <p className={cn(columnHeaderClassName, 'px-2')}>{t`Translated list value`}</p>
            <p className={cn(columnHeaderClassName, 'pl-2')}>{t`Match confidence`}</p>
            <p className={cn(rowClassName, 'px-2')}>{listValueTranslation.candidate}</p>
            <p className={cn(rowClassName, 'px-2')}>{listValueTranslation.reference}</p>
            <div className={cn(rowClassName, 'px-2')}>
              <ReviewItemSelect
                value={selectedListValue}
                options={listValueTranslationOptions}
                onChange={onListValueChange}
              />
            </div>
            <p className={cn(rowClassName, 'pl-2')}>
              {listValueTranslation.confidence ? `${listValueTranslation.confidence * 100}%` : null}
            </p>
          </div>
        </>
      ) : null}

      {!!description && (
        <div className="px-5 py-4 mt-8 bg-[#8052FF]/5">
          <p className="text-[#8052FF] font-medium text-xs  mb-2">
            <TwoStarsSVG className="mr-1 mb-[2px] inline-block" />
            {t`Explanation`}
          </p>
          <p className="text-sm">{description}</p>
        </div>
      )}

      {showExcludeWarning && (
        <div className="px-5 py-4 mt-8 bg-slate-100">
          <p className="text-slate-600 font-medium text-xs  mb-2">{t`Heads up!`}</p>
          <p className="text-sm">
            {t`This field has list values. If you exclude this field, you won't be able to review the list values related to it.`}
          </p>
        </div>
      )}
    </div>
  )
}

const columnHeaderClassName =
  'col-span-1 border-b border-slate-300 pb-1 text-[#30505A] text-sm font-semibold tracking-tight flex items-end'
const rowClassName = 'col-span-1 pt-2 text-sm'

const TRANSLATION_STATUS_LABELS: Record<TranslationStatus, string> = {
  [TranslationStatus.Match]: 'match',
  [TranslationStatus.NoMatch]: 'no match',
  [TranslationStatus.Reviewed]: 'reviewed',
  [TranslationStatus.Excluded]: 'excluded',
}
